import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Obsesie Recently Viewed: reusable on product and collection templates.
 * Reads Horizon's existing viewing history, then requests Shopify-rendered
 * resource cards once the section approaches the viewport.
 * No third-party API, no timers, no extra tracking.
 */
const initRecentlyViewed = () => {
  document.querySelectorAll('[data-ob-recently-viewed]:not([data-ob-rv-initialized])').forEach((section) => {
    section.dataset.obRvInitialized = 'true';

    let history;
    try {
      history = RecentlyViewed.getProducts();
    } catch {
      return;
    }
    if (!Array.isArray(history)) return;

    const currentId = section.dataset.currentProductId || '';
    const limit = Math.min(4, Math.max(2, Number(section.dataset.limit) || 4));
    const ids = [...new Set(history.map((id) => String(id)).filter((id) => /^\d+$/.test(id)))]
      .filter((id) => id !== currentId)
      .slice(0, limit);

    if (ids.length === 0) return;

    const load = async () => {
      try {
        const url = new URL(section.dataset.searchUrl || '/search', location.origin);
        // Preserve the unpublished theme preview while asking Shopify for product cards.
        const previewThemeId = new URL(location.href).searchParams.get('preview_theme_id');
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
        url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
        url.searchParams.set('resources[type]', 'product');

        // Use the same tested section-rendering path as Horizon's predictive search.
        // Only request once the section enters the viewport; reuse this page's product history.
        const markup = await sectionRenderer.getSectionHTML('predictive-search', false, url);
        if (!markup || !section.isConnected) return;
        const fragment = new DOMParser().parseFromString(markup, 'text/html');
        const cards = Array.from(fragment.querySelectorAll(
          '.recently-viewed-wrapper .predictive-search-results__card--product'
        ));

        const list = section.querySelector('.ob-rv-list');
        const content = section.querySelector('.ob-rv-content');
        if (!list || !content || !section.isConnected) return;

        const seen = new Set();
        const items = [];
        for (const card of cards) {
          const anchor = card.querySelector('.resource-card__link[href]');
          if (!anchor) continue;
          const href = anchor.getAttribute('href');
          if (!href || seen.has(href) || !href.includes('/products/')) continue;
          seen.add(href);
          card.className = 'ob-rv-item';
          card.removeAttribute('ref');
          // Keep the primary Shopify-responsive image only, to avoid redundant image downloads.
          card.querySelectorAll('.resource-card__image--secondary').forEach((image) => image.remove());
          items.push(card);
          if (items.length >= ids.length) break;
        }
        if (!items.length) return;

        list.replaceChildren(...items);
        content.hidden = false;
        if (items.length <= 2) section.classList.add('ob-rv-static');

        const back = section.querySelector('[data-ob-rv-back]');
        const forward = section.querySelector('[data-ob-rv-forward]');
        if (!back || !forward) return;

        const refreshButtons = () => {
          const max = Math.max(0, list.scrollWidth - list.clientWidth);
          back.disabled = list.scrollLeft <= 2;
          forward.disabled = list.scrollLeft >= max - 2;
        };

        const scrollCards = (direction) => {
          const first = list.firstElementChild;
          if (!first) return;
          const gap = parseFloat(getComputedStyle(list).columnGap) || 12;
          const distance = first.getBoundingClientRect().width + gap;
          const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
          list.scrollBy({ left: direction * distance, behavior: reduced ? 'auto' : 'smooth' });
        };

        back.addEventListener('click', () => scrollCards(-1));
        forward.addEventListener('click', () => scrollCards(1));
        list.addEventListener('scroll', refreshButtons, { passive: true });
        window.addEventListener('resize', refreshButtons, { passive: true });
        requestAnimationFrame(refreshButtons);
      } catch {
        // Keep the personalized section hidden if a browser blocks storage
        // or if Shopify search is temporarily unavailable.
      }
    };

    if (!('IntersectionObserver' in window)) {
      load();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      load();
    }, { rootMargin: '500px 0px' });
    observer.observe(section);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecentlyViewed, { once: true });
} else {
  initRecentlyViewed();
}
document.addEventListener('shopify:section:load', initRecentlyViewed);
